/*
 *
 * SaveForLater
 *
 */

import React, { useEffect, useState } from 'react';
import Modal from 'react-responsive-modal';
import {
  FiCheck,
  FiCopy,
  FiFacebook,
  FiPrinter,
  FiTwitter,
  RiMailSendLine,
  TiSocialPinterest,
} from 'react-icons/all';
import {
  resetSaveModeAction,
  saveFurnitureRequestAction,
} from '../UserActions/actions';
import { useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { SaveMode } from '../UserActions/constants';
import {
  makeSelectIsSubmittingFurniture,
  makeSelectSaveModeFurniture,
} from '../UserActions/selectors';
import { RootState } from 'containers/Header/types';
import styled from 'styled-components/macro';
import { CircleSpinner } from 'react-spinners-kit';
import { makeSelectFurniture } from '../Configurator/selectors/furniture';
import { Furniture } from '../../lib/modelBuilder/entities/Furniture';
import { displayCabinetReference } from '../../helpers/tools';
import { Cabinet } from '../../lib/modelBuilder/entities/Cabinet';
import Img from '../../components/Img';
import { useFormik } from 'formik';
import { SITE_BASE_URL } from '../App/constants';
import i18n from '../../i18n';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { basicRequest } from '../../utils/requests';
import properties from '../../helpers/config';

export const BASE_URL = `${properties.baseApiUrL}`;

enum InternalState {
  WaitingSave = 1,
  FormDisplayed = 2,
  FormSent = 3,
  Closed = 4,
}

interface StateProps {
  submitting: boolean;
  saveMode?: SaveMode;
  furniture: Furniture | null;
}

interface Props {
  show: any;
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  submitting: makeSelectIsSubmittingFurniture(),
  saveMode: makeSelectSaveModeFurniture(),
  furniture: makeSelectFurniture(),
});

function SaveForLater({ show }: Props) {
  const dispatch = useDispatch();
  const [errors, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  const { submitting, saveMode, furniture } = useSelector(stateSelector);
  const [internalState, setInternalState] = useState(InternalState.WaitingSave);
  const [copiedLinkLabel, setCopiedLinkLabel] = useState<string>(t('Copy'));

  const browserAllowClipboard = navigator.clipboard;

  const cabinetOrFurniture =
    furniture === null || furniture.quantity > 1
      ? furniture
      : furniture.cabinets[0].cabinet;

  useEffect(() => {
    if (submitting && saveMode !== SaveMode.SaveForLater) {
      setInternalState(InternalState.Closed);
    }
    if (submitting && saveMode === SaveMode.SaveForLater) {
      setInternalState(InternalState.WaitingSave);
    }
    if (!submitting && saveMode === SaveMode.SaveForLater) {
      setInternalState(InternalState.FormDisplayed);

      // @ts-ignore
      if (typeof window.addProductToFavorites === 'function') {
        // @ts-ignore
        window.addProductToFavorites(cabinetOrFurniture?.prestashopId);
      }
    }
  }, [submitting, saveMode]);

  useEffect(() => {
    switch (internalState) {
      case InternalState.WaitingSave:
        saveInKewapp();
        break;
      default:
        break;
    }
  }, [internalState]);

  const onClose = () => {
    show(false);
    dispatch(resetSaveModeAction());
  };

  const saveInKewapp = () => {
    dispatch(saveFurnitureRequestAction(SaveMode.SaveForLater));
  };

  const form = useFormik({
    initialValues: {
      email: '',
      newsletter: false,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email(t('Invalid email address'))
        .required(t('The email is required')),
    }),
    onSubmit: (values, { setSubmitting }) => {
      const data = new FormData();
      data.append('save_configurator', '1');
      data.append('prestashop_id', `${cabinetOrFurniture?.prestashopId}`);
      data.append('language', i18n.language);
      data.append('email', values.email);
      data.append('newsletter', values.newsletter ? '1' : '0');
      setError(null);

      const url = `${properties.saveForLaterUrL}`;
      const options = {
        method: 'POST',
        body: data,
      };

      basicRequest(url, options)
        .then((data: any) => {
          setInternalState(InternalState.FormSent);
          setSubmitting(false);
        })
        .catch((error: any) => {
          // TODO Set error msg details
          toast.error(`An error occurred. Please try again later.`);
          setSubmitting(false);
        });
    },
  });

  const urlProduct = `${SITE_BASE_URL}/k-${cabinetOrFurniture?.reference}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(urlProduct).then(
      function() {
        setCopiedLinkLabel(t('Copied'));
        setTimeout(() => {
          setCopiedLinkLabel(t('Copy'));
        }, 2000);
      },
      function(err) {
        // alert(
        //   'Impossible de copier le lien automatiquement. Vous pouvez le faire manuellement.',
        // );
      },
    );
  };

  if (cabinetOrFurniture === null) return null;

  const waitingSaveModalContent = () => (
    <StyledContainer className="flex flex-col items-center text-2xl text-gray-700 py-16 w-72 md:w-128">
      <div>{t('We are saving your project')}</div>
      <div className="pt-8">
        <CircleSpinner size={40} color="#4a5568" />
      </div>
    </StyledContainer>
  );

  const saveModalContent = () => (
    <StyledContainer className="flex flex-wrap">
      <div className="w-full flex flex-wrap items-center">
        <div className="sethidden md:block md:w-1/2">
          {cabinetOrFurniture.image && (
            <Img src={cabinetOrFurniture.image} alt="Image" />
          )}
        </div>
        <div className="w-full md:w-1/2 flex flex-col px-2 py-2">
          <div className="py-2 mb-2">
            <div className="title text-2xl py-1 text-gray-700">
              {t('Keep your project in your mailbox')}
            </div>
            <div className="mt-1 py-1 text-l  text-gray-700">
              {t('Come back later to finish it')}
            </div>
          </div>

          <form onSubmit={form.handleSubmit} className="w-full">
            <input
              type="text"
              id="email"
              name="email"
              placeholder="E-mail"
              onChange={form.handleChange}
              value={form.values.email}
              className="py-2 px-2 border border-gray-400 text-xl w-full"
              autoFocus
            />
            {form.touched.email && form.errors.email ? (
              <div className="text-red-dark">{form.errors.email}</div>
            ) : null}
            <div className="flex items-center">
              <input
                type="checkbox"
                name="newsletter"
                id="newsletter"
                onChange={form.handleChange}
                value={form.values.newsletter ? 1 : 0}
                className="w-6 h-6"
              />
              <label
                htmlFor="newsletter"
                className="ml-3 py-2 text-xs text-gray-600 leading-tight font-normal mb-0"
              >
                {t('subscription newsletter')}
              </label>
            </div>
            <button
              type="submit"
              className="flex bg-main text-white justify-center items-center mt-3 py-2 my-2 w-full"
              disabled={form.isSubmitting}
            >
              {!form.isSubmitting ? (
                <>
                  <RiMailSendLine className="h-6 w-6" />
                  <div className="text-xl ml-3">{t('Get my project')}</div>
                </>
              ) : (
                <div className="flex justify-center items-center">
                  <CircleSpinner size={40} color="white" />
                </div>
              )}
            </button>

            <div className="text-xs mt-2 text-gray-600">
              {t('email third parties')}
            </div>
          </form>
        </div>
      </div>
      <StyledRefPart className="border-gray-600 w-full pt-2 mt-2 text-gray-700 flex items-center">
        <div className="mr-2 flex items-center">
          <div className="mr-1">{t('Reference of your project')} :</div>
          <strong>
            {cabinetOrFurniture instanceof Cabinet
              ? displayCabinetReference(cabinetOrFurniture.reference)
              : cabinetOrFurniture.reference}
          </strong>
        </div>
        <button
          onClick={() => document.getElementById('reacttoprint')?.click()}
        >
          <FiPrinter className="h-5 w-5" />
        </button>
      </StyledRefPart>
    </StyledContainer>
  );

  const shareModalContent = () => (
    <StyledContainer className="flex text-gray-800">
      <div className="sethidden md:block md:w-1/2">
        {cabinetOrFurniture.image && (
          <Img src={cabinetOrFurniture.image} alt="Image" />
        )}
      </div>
      <div className="w-full md:w-1/2 flex flex-col px-2 py-2">
        <div className="flex items-center">
          <FiCheck className="h-12 w-12 text-green" />
          <div className="ml-3 text-xl py-1">
            {t('Your project has been sent to you')}
          </div>
        </div>
        <div className="flex items-center py-1">
          <div className="text-base">{t('Reference')} :</div>
          <div className="text-lg ml-3 font-bold">
            {cabinetOrFurniture instanceof Cabinet
              ? displayCabinetReference(cabinetOrFurniture.reference)
              : cabinetOrFurniture.reference}
          </div>
        </div>

        <div className="text-base mt-5">
          {t('Let your loved ones discover it')}
        </div>
        <div
          onClick={copyToClipboard}
          className="border border-gray-400 py-1 px-2"
        >
          kewlox.com/k-{cabinetOrFurniture.reference}
        </div>

        <div className="flex w-full">
          {browserAllowClipboard && (
            <button
              onClick={copyToClipboard}
              className="w-1/2 flex justify-center items-center my-3 py-1 px-2 border border-gray-600"
            >
              <FiCopy className="h-6 w-6" />
              <div className="ml-3 text-base">{copiedLinkLabel}</div>
            </button>
          )}
          <button
            onClick={() => document.getElementById('reacttoprint')?.click()}
            className={
              browserAllowClipboard
                ? 'w-1/2 ml-3 flex justify-center items-center my-3 py-1 px-2 border border-gray-600'
                : 'w-full flex justify-center items-center my-3 py-1 px-2 border border-gray-600'
            }
          >
            <FiPrinter className="h-6 w-6" />
            <div className="ml-3 text-base">{t('Print')}</div>
          </button>
        </div>

        <div className="mt-6 flex w-full justify-center items-center">
          <a
            target="_blank"
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
              urlProduct,
            )}`}
          >
            <FiFacebook className="h-8 w-8 mx-3" />
          </a>
          <a
            target="_blank"
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
              cabinetOrFurniture.getName(i18n.language),
            )}&url=${encodeURIComponent(urlProduct)}`}
          >
            <FiTwitter className="h-8 w-8 mx-3" />
          </a>
          <a
            target="_blank"
            href={`https://pinterest.com/pin/create/link/?url=${encodeURIComponent(
              urlProduct,
            )}&media=${
              cabinetOrFurniture.image
                ? encodeURIComponent(cabinetOrFurniture.image)
                : ''
            }&description=${encodeURIComponent(
              cabinetOrFurniture.getName(i18n.language),
            )}`}
          >
            <TiSocialPinterest className="h-10 w-10 mx-3" />
          </a>
        </div>
      </div>
    </StyledContainer>
  );

  return (
    <Modal open={true} onClose={onClose} center>
      {internalState === InternalState.WaitingSave && waitingSaveModalContent()}
      {internalState === InternalState.FormDisplayed && saveModalContent()}
      {internalState === InternalState.FormSent && shareModalContent()}
    </Modal>
  );
}

const StyledContainer = styled.div`
  //width: 32rem;
  //@media (min-width: 768px) {
  //  width: 40rem;
  //}
`;

const StyledRefPart = styled.div`
  border-top-width: 1px;
`;

export default SaveForLater;
